import { useEffect, useRef, useState } from 'react'
import Constants from '../../../app/utils/constants'
import colors from '../../theme/color'

const headingGradientStyle = {
  background: 'linear-gradient(92.21deg, #EA892A 0%, #E35384 100%)',
  WebkitBackgroundClip: 'text',
  backgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
}

type ComplianceCardData = {
  title: string
  points: string[]
}

const ComplianceExpertiseCard = ({ card }: { card: ComplianceCardData }) => (
  <div className="relative overflow-hidden flex h-full w-full text-start items-start justify-start flex-col rounded-3xl border border-purple/20 bg-[#130C2F]/90 shadow-[0_12px_40px_rgba(0,0,0,0.35)] p-4 sm:p-3 md:p-4 lg:p-5">
    <div
      className="pointer-events-none absolute top-0 left-0 h-24 w-24 sm:h-28 sm:w-28 md:h-32 md:w-32 lg:h-36 lg:w-36 opacity-20"
      style={{
        background:
          'linear-gradient(134.31deg, #744DFF 0%, rgba(69, 46, 153, 0) 60.89%)',
      }}
    />

    <h3
      className="font-gilroy-bold text-xs sm:text-sm md:text-lg lg:text-xl xl:text-2xl 2xl:text-3xl"
      style={headingGradientStyle}
    >
      {card.title}
    </h3>

    <ul className="mt-1 list-disc pl-3 space-y-0.5 font-gilroy-medium leading-relaxed text-white text-[10px] sm:text-[11px] md:text-sm lg:text-sm xl:text-base 2xl:text-xl">
      {card.points.map((p) => (
        <li key={p}>{p}</li>
      ))}
    </ul>
  </div>
)

const ComplianceExpertise = () => {
  const { heading, centerImage, topCards, bottomCards } =
    Constants.healthcareComplianceExpertise

  const leftCards = topCards.slice(0, 2)
  const rightCards = bottomCards.slice(0, 2)

  const sectionRef = useRef<HTMLDivElement | null>(null)
  const [visible, setVisible] = useState(false)

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setVisible(entry.isIntersecting)
      },
      { threshold: 0.3 }
    )

    if (sectionRef.current) observer.observe(sectionRef.current)

    return () => observer.disconnect()
  }, [])

  return (
    <section className="w-full py-10 md:py-12 lg:py-14">

      {/* 🔥 ANIMATION CSS */}
      <style>{`
        .left-enter,
        .right-enter,
        .center-enter {
          transform: scale(0.6);
          opacity: 0;
        }
        
        .show {
          transform: scale(1);
          opacity: 1;
        
          transition:
            transform 0.8s cubic-bezier(0.22, 1, 0.36, 1),
            opacity 0.8s ease;
        }

        /* 🔥 ROTATING GRADIENT BACKGROUND */
        .rotating-bg {
          position: relative;
          overflow: hidden;
          border-radius: 30px;
        }

        .rotating-bg::before {
          content: '';
          position: absolute;
          inset: -50%;
          background:
            radial-gradient(ellipse at top left,    #ea8a2a86 0%, transparent 55%),
            radial-gradient(ellipse at top right,   #e3538392 0%, transparent 55%),
            radial-gradient(ellipse at bottom left, #5c30fd3a 0%, transparent 50%),
            radial-gradient(ellipse at bottom right,#5c30fd3a 0%, transparent 50%);
          
          animation: rotateBg 6s linear infinite; /* 🔥 SPEED */
          // filter: blur(0px);
          opacity: 1;
        }

        @keyframes rotateBg {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }

        .rotating-bg > * {
          position: relative;
          z-index: 1;
        }

        .floatY {
  animation: floatY 3s ease-in-out infinite;
}

@keyframes floatY {
  0%   { transform: translateY(0px); }
  50%  { transform: translateY(-15px); }
  100% { transform: translateY(0px); }
}
      `}</style>

      {/* Heading */}
      <h2 className="px-4 text-center font-rajdhani font-semibold text-white text-2xl sm:text-3xl md:text-4xl lg:text-5xl md:px-8 lg:px-12">
        {heading}
      </h2>

     
      {/* Main layout */}
<div
  ref={sectionRef}
  className="mt-6 w-full px-3 py-6 sm:px-4 md:px-6 lg:px-8 xl:py-12"
  style={{ backgroundColor: colors.complianceOverlay }}
>

  {/* MOBILE VIEW */}
<div className="flex flex-col items-center gap-5 md:hidden">

{/* TOP IMAGE */}
<div
  className={`
    flex items-center justify-center
    w-full max-w-[310px]
    transition-all duration-700
    ${visible ? 'show' : 'center-enter'}
  `}
>
  <div
    className="
      rotating-bg
      flex items-center justify-center
      w-full min-h-[310px]
    "
    style={{ backgroundColor: '#1a0e3a' }}
  >
    <img
      src={centerImage}
      alt={heading}
      className="floatY h-auto w-[55%] object-contain"
    />
  </div>
</div>

{/* CARDS GRID */}
<div className="grid w-full grid-cols-2 gap-3">

  {[...leftCards, ...rightCards].map((card, index) => (
    <div
      key={card.title}
      className={`transition-all duration-700 ${
        visible
          ? 'show'
          : index % 2 === 0
            ? 'left-enter'
            : 'right-enter'
      }`}
    >
      <ComplianceExpertiseCard card={card} />
    </div>
  ))}

</div>
</div>

  {/* TABLET + DESKTOP VIEW */}
  <div className="hidden md:grid grid-cols-[1fr_auto_1fr] gap-2 sm:gap-3 md:gap-4 lg:gap-6 items-stretch max-w-[90%] md:max-w-[95%] lg:max-w-[95%] xl:max-w-[90%] 2xl:max-w-[85%] max-h-[500px] md:max-h-[500px] lg:max-h-[500px] xl:max-h-[700px] 2xl:max-h-[800px] mx-auto">

    {/* LEFT COLUMN */}
    <div
      className={`flex flex-col gap-2 sm:gap-3 md:gap-4 lg:gap-6 transition-all duration-700 ${
        visible ? 'show' : 'left-enter'
      }`}
    >
      {leftCards.map((card) => (
        <div key={card.title} className="flex-1">
          <ComplianceExpertiseCard card={card} />
        </div>
      ))}
    </div>

    {/* CENTER IMAGE */}
    <div
      className={`flex self-stretch items-center justify-center 
        w-[90px] sm:w-[130px] md:w-[240px] lg:w-[340px] xl:w-[400px] 2xl:w-[550px] 
        transition-all duration-700
        ${visible ? 'show' : 'center-enter'}
      `}
    >
      <div
        className="
          w-full h-full rotating-bg
          flex items-center justify-center
          min-h-[140px] sm:min-h-[180px] md:min-h-[260px] lg:min-h-[340px] xl:min-h-[380px] 2xl:min-h-[550px]
        "
        style={{ backgroundColor: '#1a0e3a' }}
      >
        <img
          src={centerImage}
          alt={heading}
          className="object-contain w-[60%] sm:w-[55%] md:w-[80%] lg:w-[65%] xl:w-[60%] 2xl:w-[60%] h-auto floatY"
        />
      </div>
    </div>

    {/* RIGHT COLUMN */}
    <div
      className={`flex flex-col gap-2 sm:gap-3 md:gap-4 lg:gap-6 transition-all duration-700 ${
        visible ? 'show' : 'right-enter'
      }`}
    >
      {rightCards.map((card) => (
        <div key={card.title} className="flex-1">
          <ComplianceExpertiseCard card={card} />
        </div>
      ))}
    </div>

  </div>
</div>
    </section>
  )
}

export default ComplianceExpertise